import { useEffect } from "react";
import { toast } from "sonner";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const params = () => new URLSearchParams(window.location.search);
const hiddenFrame = (src) => {
  const f = document.createElement("iframe");
  f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  f.src = src;
  return f;
};

// PDF blob: Chrome fires onload only after the PDF viewer has the document.
function printPdfInIframe(src) {
  const f = hiddenFrame(src);
  f.onload = () => {
    setTimeout(() => {
      try { f.contentWindow.focus(); f.contentWindow.print(); }
      catch { window.open(src, "_blank"); }
      setTimeout(() => { f.remove(); URL.revokeObjectURL(src); }, 60000);
    }, 800);
  };
  document.body.appendChild(f);
}

// Prints a PDF blob. On iPhone/iPad opens the PDF (Safari shows print/share there).
export function printPdfBlob(blob, preopened) {
  const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
  if (isIOS()) {
    if (preopened) preopened.location.href = url; else window.open(url, "_blank");
    return;
  }
  if (preopened) preopened.close();
  printPdfInIframe(url);
}

// For async loads: call synchronously in the tap handler, then pass to printPdfBlob.
export const preopenForIOS = () => (isIOS() ? window.open("", "_blank") : null);

// Prints an internal app page. The page calls usePrintReady() and signals when data + images are loaded.
export function printAppPage(path) {
  const sep = path.includes("?") ? "&" : "?";
  if (isIOS()) { window.open(`${path}${sep}print=1`, "_blank"); return; }
  const f = hiddenFrame(`${path}${sep}embed=1`);
  const toastId = toast.loading("Preparando impressão…");
  let done = false;
  const finish = (err) => {
    if (done) return;
    done = true;
    window.removeEventListener("message", onMsg);
    clearTimeout(timer);
    toast.dismiss(toastId);
    if (err) { toast.error(err); f.remove(); return; }
    try { f.contentWindow.focus(); f.contentWindow.print(); } catch { window.open(`${path}${sep}print=1`, "_blank"); }
    setTimeout(() => f.remove(), 60000);
  };
  const onMsg = (e) => {
    if (e.origin !== window.location.origin || e.source !== f.contentWindow) return;
    if (e.data?.type === "print-ready") finish(null);
    if (e.data?.type === "print-error") finish(e.data.message || "Não foi possível carregar os dados para imprimir");
  };
  const timer = setTimeout(() => finish("A impressão demorou demais para carregar. Tente de novo."), 20000);
  window.addEventListener("message", onMsg);
  document.body.appendChild(f);
}

export const wantsAutoPrint = () => params().get("print") === "1";

const waitImages = () => Promise.all(Array.from(document.images).map((img) => (img.complete ? null : new Promise((res) => {
  img.addEventListener("load", res, { once: true });
  img.addEventListener("error", res, { once: true });
  setTimeout(res, 5000);
}))));

// Call in print pages with "loading" | "ready" | "error". Prints only after data, fonts and images are loaded.
export function usePrintReady(state, errorMsg) {
  useEffect(() => {
    const embed = params().get("embed") === "1";
    if (!embed && !wantsAutoPrint()) { return undefined; }
    if (state === "error") {
      if (embed) window.parent.postMessage({ type: "print-error", message: errorMsg }, window.location.origin);
      return undefined;
    }
    if (state !== "ready") { return undefined; }
    let cancelled = false;
    (async () => {
      await (document.fonts?.ready || Promise.resolve());
      await waitImages();
      await new Promise((r) => setTimeout(r, 300));
      if (cancelled) return;
      if (embed) window.parent.postMessage({ type: "print-ready" }, window.location.origin);
      else window.print();
    })();
    return () => { cancelled = true; };
  }, [state, errorMsg]);
}
